/**
 * Web Push toggle cho self-host PHP (port từ Phase 7 PushNotificationToggle).
 *  - Lấy VAPID public key từ /api/push/vapid-public (admin chủ động đổi trong app.php)
 *  - Subscribe → POST /api/push/subscribe
 *  - Unsubscribe → POST /api/push/unsubscribe
 *  - Test/trigger → POST /api/push/test | /api/push/trigger
 */
import { useEffect, useState } from "react";
import { Bell, BellOff, Loader2, Send } from "lucide-react";
import { api } from "@/lib/api";

function b64ToUint8(b64: string): Uint8Array {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const s = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(s);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function supported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function PushToggle({ isAdmin }: { isAdmin: boolean }) {
  const [sub, setSub] = useState<PushSubscription | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [ok, setOk] = useState(true);

  useEffect(() => {
    if (!supported()) { setOk(false); return; }
    navigator.serviceWorker.register("/sw.js").catch(() => setOk(false));
    navigator.serviceWorker.ready
      .then((r) => r.pushManager.getSubscription())
      .then((s) => setSub(s))
      .catch(() => {});
  }, []);

  if (!ok || !isAdmin) return null;

  async function toggle() {
    setLoading(true);
    try {
      if (sub) {
        try { await sub.unsubscribe(); } catch (_) {}
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        setSub(null);
        return;
      }
      if (Notification.permission === "denied") { alert("Trình duyệt đã chặn thông báo"); return; }
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { alert("Cần cấp quyền thông báo"); return; }
      const cfg = await api.get<{ key: string }>("/push/vapid-public");
      if (!cfg.key) { alert("Server chưa cấu hình VAPID key"); return; }
      const reg = await navigator.serviceWorker.ready;
      const s = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64ToUint8(cfg.key) as BufferSource,
      });
      const json = s.toJSON() as { keys?: { p256dh?: string; auth?: string } };
      if (!json.keys?.p256dh || !json.keys?.auth) throw new Error("Thiếu khoá đăng ký");
      await api.post("/push/subscribe", {
        endpoint: s.endpoint,
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
        userAgent: navigator.userAgent.slice(0, 500),
      });
      setSub(s);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Lỗi đăng ký push");
    } finally { setLoading(false); }
  }

  async function runTest(kind: "ping" | "morning" | "evening") {
    setTesting(true);
    try {
      const path = kind === "ping" ? "/push/test" : "/push/trigger";
      const body = kind === "ping" ? {} : { when: kind };
      const res = await api.post<{ sent?: number; reason?: string }>(path, body);
      if (res.reason) alert(res.reason);
      else alert(`Đã gửi ${res.sent ?? 0} thông báo`);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Lỗi gửi thử");
    } finally { setTesting(false); }
  }

  const btn = "inline-flex items-center gap-1.5 h-8 px-2 rounded-md text-xs border";
  return (
    <div className="flex items-center gap-1">
      <button onClick={toggle} disabled={loading}
        className={`${btn} ${sub ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:bg-accent"}`}
        title={sub ? "Tắt thông báo trên thiết bị này" : "Bật thông báo nhắc việc"}>
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : sub ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
        <span className="hidden sm:inline">{sub ? "Đã bật nhắc" : "Bật nhắc"}</span>
      </button>
      {sub && (
        <>
          <button onClick={() => runTest("ping")} disabled={testing} className={`${btn} bg-card border-border hover:bg-accent`} title="Gửi 1 thông báo thử ngay">
            {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
          </button>
          <button onClick={() => runTest("morning")} disabled={testing} className={`${btn} bg-card border-border hover:bg-accent`} title="Quét việc hôm nay">Hôm nay</button>
          <button onClick={() => runTest("evening")} disabled={testing} className={`${btn} bg-card border-border hover:bg-accent`} title="Quét việc ngày mai">Ngày mai</button>
        </>
      )}
    </div>
  );
}
